import { useEffect, useRef, useState } from 'react';
import { VideoPlayerModal } from '../../common/VideoPlayer';
import Reveal from '../../common/Reveal';
import { buildUrlVideoTarget } from '../../../utils/vdocipherMappers';
import ViewAllButton from '../shared/ViewAllButton';

const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1557683316-973673baf926?w=300&h=450&fit=crop';

const CARD_WIDTH = 169;
const CARD_HEIGHT = 300;
const VISIBLE_COUNT = 6;
const GAP_PX = 20; // matches `gap-5` (Tailwind default 1 = 4px => gap-5 = 20px)
const SCROLL_STEP_PX = (CARD_WIDTH + GAP_PX) * VISIBLE_COUNT;

function SnapshotCard({ item, onOpen }) {
  const hasThumbnail = Boolean(item.image);
  const hasVideo = Boolean(item.videoUrl);

  return (
    <button
      type="button"
      onClick={() => (hasVideo ? onOpen(item) : null)}
      disabled={!hasVideo}
      className="group relative block h-[300px] w-[169px] overflow-hidden rounded-2xl bg-zinc-800 text-left disabled:cursor-not-allowed disabled:opacity-60"
      aria-label={item.title || item.alt}
    >
      {hasThumbnail ? (
        <img
          src={item.image}
          alt={item.alt || item.title}
          className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
          onError={(e) => {
            e.currentTarget.src = FALLBACK_IMAGE;
          }}
        />
      ) : hasVideo ? (
        <video
          src={item.videoUrl}
          muted
          playsInline
          preload="metadata"
          className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
        />
      ) : (
        <img
          src={FALLBACK_IMAGE}
          alt={item.alt || item.title}
          className="h-full w-full object-cover"
        />
      )}

      <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/25 transition group-hover:bg-black/35">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90 text-violet-600 shadow-lg">
          <svg className="ml-0.5 h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path d="M8 5v14l11-7z" />
          </svg>
        </span>
      </span>

      {item.title && (
        <span className="pointer-events-none absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-3 pb-3 pt-8 text-sm font-medium text-white">
          {item.title}
        </span>
      )}
    </button>
  );
}

function ConceptSnapshotsSection({ videos, loading, error }) {
  const scrollRef = useRef(null);
  const list = videos || [];
  const [activeVideo, setActiveVideo] = useState(null);

  const scroll = (direction) => {
    scrollRef.current?.scrollBy({
      left: direction === 'left' ? -SCROLL_STEP_PX : SCROLL_STEP_PX,
      behavior: 'smooth',
    });
  };

  const needsScroll = list.length > VISIBLE_COUNT;
  const showArrows = list.length > VISIBLE_COUNT;

  useEffect(() => {
    if (!activeVideo) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setActiveVideo(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activeVideo]);

  const openVideo = (item) => setActiveVideo(item);
  const closeVideo = () => setActiveVideo(null);

  const activeVideoTarget = activeVideo
    ? buildUrlVideoTarget({
        title: activeVideo.title,
        videoUrl: activeVideo.videoUrl,
      })
    : null;

  return (
    <section className="bg-zinc-900 px-4 py-7 sm:px-6 sm:py-8 lg:px-8 lg:py-10">
      <div className="mx-auto max-w-7xl">
        <div className="relative mb-8 text-center sm:mb-10">
          <Reveal variant="up">
            <h2 className="text-2xl font-semibold text-white sm:text-[32px]">Concept Snapshots</h2>
          </Reveal>
          {showArrows && (
            <div className="absolute right-0 top-1/2 hidden -translate-y-1/2 gap-2 sm:flex">
              <button
                type="button"
                onClick={() => scroll('left')}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-violet-400/60 text-violet-300 transition hover:bg-violet-500/20"
                aria-label="Previous snapshots"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => scroll('right')}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-violet-400/60 text-violet-300 transition hover:bg-violet-500/20"
                aria-label="Next snapshots"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}
        </div>

        <div>
          {loading && !list.length ? (
            <div className="flex justify-center gap-5 overflow-hidden">
              {[1, 2, 3, 4].map((n) => (
                <div
                  key={n}
                  className="h-[300px] w-[169px] shrink-0 animate-pulse rounded-2xl bg-white/10"
                />
              ))}
            </div>
          ) : error ? (
            <p className="text-center text-sm text-red-400">{error}</p>
          ) : list.length > 0 ? (
            <div
              ref={scrollRef}
              className={`overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden`}
            >
              <div
                className={`flex gap-5 ${needsScroll ? 'w-max' : 'w-full justify-center'}`}
              >
                {list.map((item, index) => (
                  <Reveal key={item?.id ?? `${item?.title ?? 'item'}-${index}`} variant="zoom" delay={(index % 4) * 80}>
                    <SnapshotCard item={item} onOpen={openVideo} />
                  </Reveal>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-center text-sm text-white/60">No concept reels available.</p>
          )}
        </div>

        <div className="mt-10 flex justify-center">
          <ViewAllButton label="Explore Our Edu Reels" variant="dark" />
        </div>
      </div>

      <VideoPlayerModal
        target={activeVideoTarget}
        isOpen={Boolean(activeVideoTarget)}
        onClose={closeVideo}
      />
    </section>
  );
}

export default ConceptSnapshotsSection;
